/**
 * Guest checkout session.
 *
 * Holds the contact info (name/email/phone) a guest gives before paying, and
 * doubles as the signal that a guest has committed to the server-side guest
 * cart (see backend/src/middleware/cartIdentity.ts) instead of the
 * sessionStorage anonymous cart used while just browsing. Once this contact
 * is set, cartStore.ts routes cart reads/writes to the backend instead of
 * sessionStorage, mirroring the authenticated flow but keyed by the
 * `guestCartId` httpOnly cookie instead of a Cognito sub.
 */

export interface GuestContact {
  name: string;
  email: string;
  phone?: string;
}

const STORAGE_KEY = 'guestCheckoutContact';

/** Read the guest's contact info, or null if no guest checkout is active. */
export const getGuestContact = (): GuestContact | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as GuestContact;
    if (!parsed?.name || !parsed?.email) return null;

    return parsed;
  } catch (error) {
    console.error('Failed to parse guest checkout contact:', error);
    return null;
  }
};

/**
 * Store the guest's contact info, starting (or updating) a guest checkout
 * session. The email is lowercased to match how the backend stores and
 * matches guest emails.
 */
export const setGuestContact = (contact: GuestContact): void => {
  try {
    const normalized: GuestContact = {
      name: contact.name.trim(),
      email: contact.email.trim().toLowerCase(),
      ...(contact.phone?.trim() ? { phone: contact.phone.trim() } : {}),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  } catch (error) {
    console.error('Failed to save guest checkout contact:', error);
  }
};

/** End the guest checkout session (e.g. after the order completes or the buyer logs in). */
export const clearGuestCheckoutSession = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear guest checkout session:', error);
  }
};

/** Whether a guest has started checkout (and so should use the server-side guest cart). */
export const isGuestCheckoutActive = (): boolean => {
  return getGuestContact() !== null;
};
